import React from "react";
import { GetListing_listing } from "../../queries/types/GetListing";
import Box from "../../../../elements/v2/Box/Box";
import FilteredProductSlider from "./FilteredProductSlider";
import Events from "../../../../../utils/analytics";
import { getCustomFieldSize } from "../Size/Size";
import {
  getDealsFilters,
  getMoreBrandFilters,
  getMoreFromSameAuthorFilters,
  getMoreSizeFilters,
  getRecentlyViewedListingFilters,
  getSimilarFilters,
} from "../../../../../utils/algolia/filters";
import { SellerType } from "../../../../../types/graphql-global-types";
import useCustomSearch from "../../../../../utils/algolia/useCustomSearch";
import { primaryIndexName } from "../../../../../utils/algolia/searchClient";
import { HitRecord, ListingCardData } from "../../../../../utils/algolia/mapHitToListing";
import { LoadingSpinnerCentered } from "../../../../elements/v2/LoadingSpinner/LoadingSpinner";
import { SearchQuery, SearchResponse } from "@algolia/client-search/dist/node";

const MINIMUM_NUMBER_ITEMS_TO_SHOW_SLIDER = 2;

export type ProductRecommendationsProps = {
  listing?: GetListing_listing;
  countryCode?: string;
  remoteIp?: string;
};

export default function ProductRecommendations({
  listing,
  countryCode,
  remoteIp,
}: ProductRecommendationsProps) {
  const brand = listing.brand;
  const category = listing.category.name;
  const size = getCustomFieldSize(listing);

  const makeListingClickHandler = (carouselType: string) => (listing: ListingCardData) => {
    Events.sendProductCardClicked(listing, "PDP Carousel", {
      carouselType,
    });
  };

  const sameAuthorFilter = getMoreFromSameAuthorFilters(listing);
  const dealsFilter = getDealsFilters(listing);
  const similarFilter = getSimilarFilters(listing);
  const moreBrandFilter = getMoreBrandFilters(listing);
  const moreSizeFilter = getMoreSizeFilters(listing);
  const recentlyViewedListingFilter = getRecentlyViewedListingFilters(listing);

  const carouselFilters = [
    sameAuthorFilter,
    dealsFilter,
    similarFilter,
    moreBrandFilter,
    moreSizeFilter,
    recentlyViewedListingFilter,
  ];

  const requests = carouselFilters.map(
    (filters) =>
      ({
        indexName: primaryIndexName,
        hitsPerPage: 10,
        clickAnalytics: true,
        analyticsTags: ["PDP Carousel", "Custom Query"],
        filters,
      } as SearchQuery)
  );

  const { results, searching } = useCustomSearch(requests, remoteIp);

  if (searching) return <LoadingSpinnerCentered />;

  const [
    sameAuthorResults,
    dealsResults,
    similarResults,
    moreBrandResults,
    moreSizeResults,
    recentlyViewedResults,
  ] = results as SearchResponse<HitRecord>[];

  return (
    <Box id="product-recommendations">
      <FilteredProductSlider
        heading="Deals on Similar Listings"
        searchResults={dealsResults}
        handleListingClick={makeListingClickHandler("Discounted")}
        countryCode={countryCode}
      />
      <FilteredProductSlider
        heading={`${brand} ${category}`}
        searchResults={similarResults}
        handleListingClick={makeListingClickHandler("Narrow")}
        countryCode={countryCode}
      />
      {similarFilter !== moreBrandFilter && (
        <FilteredProductSlider
          heading={`More ${brand} ${category}`}
          searchResults={moreBrandResults}
          handleListingClick={makeListingClickHandler("Wider")}
          countryCode={countryCode}
        />
      )}
      {listing.author.sellerType === SellerType.professional_seller &&
        sameAuthorResults.nbHits >= MINIMUM_NUMBER_ITEMS_TO_SHOW_SLIDER && (
          <FilteredProductSlider
            heading={`More Listings from ${listing.author.displayName || listing.author.givenName}`}
            searchResults={sameAuthorResults}
            handleListingClick={makeListingClickHandler("Same Author")}
            countryCode={countryCode}
          />
        )}
      <FilteredProductSlider
        heading={`More ${size ? `Size (${size}) ` : ""}${category}`}
        searchResults={moreSizeResults}
        handleListingClick={makeListingClickHandler("Widest")}
        countryCode={countryCode}
      />
      {recentlyViewedListingFilter && (
        <FilteredProductSlider
          heading="Recently Viewed"
          searchResults={recentlyViewedResults}
          handleListingClick={makeListingClickHandler("Recently Viewed")}
          countryCode={countryCode}
        />
      )}
    </Box>
  );
}
